<template>
  <b-container fluid>
    <b-row class="mb-2">
      <b-col class="px-sm-3 px-0">
        <h1>Deliveries</h1>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="px-sm-3 px-0">
        <b-overlay :show="loading" no-wrap fixed />
        <b-form inline class="mb-2" @submit.prevent="applyFilters">
          <b-form-input
            v-model="queryFilters.search"
            placeholder="Search by delivery id or address"
            title="Search by delivery id or address"
            class="mr-2 mb-2 single-input"
          />
          <custom-search-field
            v-model="queryFilters.network"
            class="mr-2 mb-2 single-input"
            :placeholder="'Filter by network'"
            :path-to-search="'networks'"
            :label-field="'name'"
            multiple
          />
          <b-form-group class="custom-search-input mr-2 mb-2">
            <multiselect
              v-model="queryFilters.status"
              select-label=""
              open-direction="bottom"
              :options="statusOptions"
              track-by="value"
              deselect-label="remove"
              :searchable="false"
              :close-on-select="true"
              :show-no-results="false"
              :hide-selected="false"
              label="text"
              placeholder="Filter by status"
              class="single-input"
            />
          </b-form-group>
          <custom-search-field
            v-model="queryFilters.provider"
            class="mr-2 mb-2 single-input"
            :placeholder="'Filter by providers'"
            :path-to-search="'delivery-providers'"
          />
          <custom-date-picker
            :initial-range="queryFilters.range"
            class="mr-2"
            @rangeChange="onRangeChange"
          />
          <b-button type="submit" variant="primary" class="mr-2 mb-2"
            >Filter</b-button
          >
          <b-button
            variant="secondary"
            class="mr-2 mb-2"
            @click="exportDeliveries"
            >Export</b-button
          >
        </b-form>
      </b-col>
    </b-row>
    <b-row class="overflow-auto">
      <b-col class="px-sm-3 px-0">
        <b-table
          hover
          no-local-sorting
          small
          :fields="fields"
          :items="data"
          :busy="busy"
          @sort-changed="onSortingChanged"
          @row-clicked="onRowClicked"
        >
          <template #cell(id)="row">
            <router-link :to="`/deliveries/${row.item.id}`">
              {{ row.item.id }}
            </router-link>
          </template>
          <template #cell(status)="row">
            {{ row.item.status | capitalize }}
          </template>
          <template #cell(fulfillment_time)="row">
            {{ row.item.fulfillment_time | formatDateTime }}
          </template>
          <template #cell(address)="row">
            {{ row.item.address | formatAddressStreet }}
          </template>
          <template #cell(network)="row">
            {{ row.item.network.name }}
          </template>
          <template #cell(delivery_provider)="row">
            {{ row.item.delivery_provider.name }}
          </template>
          <template #cell(subtotal)="row">
            {{ (row.item.subtotal / 100) | currency }}
          </template>
        </b-table>
      </b-col>
    </b-row>
    <pagination-handler v-if="data.length" :query-data="queryFilters" />
  </b-container>
</template>

<script>
import tableMixin from '../../mixins/TableMixin';
import ResourceAPI from '../../services/ResourceAPI';

export default {
  name: 'Deliveries',
  mixins: [tableMixin],
  data() {
    return {
      fields: [
        {
          key: 'id',
          label: 'ID',
          sortable: true,
        },
        {
          key: 'status',
          label: 'Status',
          sortable: true,
        },
        {
          key: 'fulfillment_time',
          label: 'Fulfillment Time',
          sortable: true,
        },
        {
          key: 'address',
          label: 'Address',
          sortable: false,
        },
        {
          key: 'delivery_provider',
          label: 'Provider',
          sortable: true,
        },
        {
          key: 'network',
          label: 'Network',
          sortable: true,
        },
        {
          key: 'orderCount',
          label: 'Orders',
          sortable: true,
        },
        {
          key: 'subtotal',
          label: 'Revenue',
          sortable: true,
        },
      ],
      data: [],
      rowRouteName: 'delivery',
      loading: false,
      exportAlert: false,
      statusOptions: [
        {
          value: 'pending',
          text: 'Pending',
        },
        {
          value: 'preparing',
          text: 'Preparing',
        },
        {
          value: 'ready',
          text: 'Ready',
        },
        {
          value: 'enroute',
          text: 'Enroute',
        },
        {
          value: 'completed',
          text: 'Completed',
        },
        {
          value: 'canceled',
          text: 'Canceled',
        },
      ],
    };
  },
  methods: {
    onRangeChange(range) {
      this.queryFilters.range = {
        ...range,
      };
    },
    loadResults() {
      this.setRequestData();
      // Format search params for query
      const searchParams = { ...this.queryFilters };
      searchParams.status = searchParams.status
        ? searchParams.status.value
        : null;
      searchParams.fulfillmentCenter = searchParams.kitchen
        ? searchParams.kitchen.id
        : null;
      searchParams.networks = searchParams.network
        ? searchParams.network.id
        : null;
      searchParams.deliveryProvider = searchParams.provider
        ? searchParams.provider.id
        : null;
      searchParams.with = 'address,deliveryProvider,network';
      searchParams.appends = 'orderCount,subtotal';
      searchParams.start =
        searchParams.range && 'start' in searchParams.range
          ? this.formatDateFilter(searchParams.range.start)
          : null;
      searchParams.end =
        searchParams.range && 'end' in searchParams.range
          ? this.formatDateFilter(searchParams.range.end)
          : null;
      searchParams.range = null;
      searchParams.kitchen = null;
      searchParams.network = null;
      searchParams.provider = null;

      return ResourceAPI.getRecords('deliveries', searchParams)
        .then((data) => {
          this.setResponseData(data);
        })
        .catch((error) => alert(error));
    },
    exportDeliveries() {
      const me = this;

      me.loading = true;

      ResourceAPI.downloadCSV(
        'deliveries/export',
        {
          start:
            me.queryFilters.range && 'start' in me.queryFilters.range
              ? this.formatDateFilter(me.queryFilters.range.start)
              : null,
          end:
            me.queryFilters.range && 'end' in me.queryFilters.range
              ? this.formatDateFilter(me.queryFilters.range.end)
              : null,
          deliveryProvider: me.queryFilters.provider
            ? me.queryFilters.provider.id
            : null,
          networks: me.queryFilters.network ? me.queryFilters.network.id : null,
          search: me.queryFilters.search,
          status: me.queryFilters.status ? me.queryFilters.status.value : null,
        },
        'deliveries.csv'
      )
        .then((response) => {
          me.loading = false;
          if (response === false) {
            me.exportError = true;
            return;
          }
          me.exportError = false;
        })
        .catch((error) => alert(error));
    },
  },
};
</script>
